import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react";
import { sidebarStorage } from "~/utils/sidebar-storage";

export type RouteContextType = {
  isActive: (path: string) => boolean;
  isActivePrefix: (prefix: string, level?: number) => boolean;
  isChildRoute: (path: string) => boolean;
};

export type SidebarHandlers = {
  trackBrandSettings?: () => void;
  trackListsView?: () => void;
  trackPermissionsAccess?: () => void;
  toggleAdministration?: () => void;
  toggleDashboard?: () => void;
  onTaskClick?: (entityId: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: ((...args: any[]) => void) | undefined;
};

type SidebarContextValue = {
  expandedIds: Set<string>;
  toggleExpand: (entityId: string) => void;
  expandEntity: (entityId: string) => void;
  folderDialogState: FolderDialogState;
  openFolderDialog: (state: Omit<FolderDialogState, "isOpen">) => void;
  closeFolderDialog: () => void;
  routeContext: RouteContextType;
  handlers: SidebarHandlers;
};

type FolderDialogState = {
  isOpen: boolean;
  mode: "create" | "rename" | "rename-task";
  brandId?: number;
  parentFolderId?: number | null;
  folderId?: number;
  taskId?: number;
  folderName?: string;
  taskName?: string;
};

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

type SidebarProviderProps = {
  children: ReactNode;
  routeContext: RouteContextType;
  handlers: SidebarHandlers;
};

export const SidebarProvider = ({ children, routeContext, handlers }: SidebarProviderProps) => {
  const [folderDialogState, setFolderDialogState] = useState<FolderDialogState>({
    isOpen: false,
    mode: "create",
  });

  const openFolderDialog = useCallback((state: Omit<FolderDialogState, "isOpen">) => {
    setFolderDialogState({ ...state, isOpen: true });
  }, []);

  const closeFolderDialog = useCallback(() => {
    setFolderDialogState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    sidebarStorage.clearExpiredCache();
    const loaded = sidebarStorage.loadExpandedState();
    return loaded;
  });

  const setExpanded = useCallback((entityId: string, expanded: boolean) => {
    setExpandedIds((prev) => {
      const has = prev.has(entityId);
      if (has === expanded) return prev;

      const next = new Set(prev);
      if (expanded) {
        next.add(entityId);
      } else {
        next.delete(entityId);
      }

      sidebarStorage.saveExpandedState(next);
      return next;
    });
  }, []);

  const toggleExpand = useCallback(
    (id: string) => setExpanded(id, !expandedIds.has(id)),
    [setExpanded, expandedIds]
  );
  const expandEntity = useCallback((id: string) => setExpanded(id, true), [setExpanded]);

  const value = useMemo(
    () => ({
      expandedIds,
      toggleExpand,
      expandEntity,
      folderDialogState,
      openFolderDialog,
      closeFolderDialog,
      routeContext,
      handlers,
    }),
    [
      expandedIds,
      toggleExpand,
      expandEntity,
      folderDialogState,
      openFolderDialog,
      closeFolderDialog,
      routeContext,
      handlers,
    ]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSidebarContext = (): SidebarContextValue => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebarContext must be used within a SidebarProvider");
  }
  return context;
};
